import Popper, { Placement, PopperOptions } from 'popper.js';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

function on(element?: Node, event?: EventType, handler?: EventHandlerType) {
  if (element && event && handler) {
    element.addEventListener(event, handler, false);
  }
}

function off(element: Node, event: EventType, handler: EventHandlerType) {
  if (element && event) {
    element.removeEventListener(event, handler, false);
  }
}

type EventType =
  | 'click'
  | 'mouseover'
  | 'focus'
  | 'mouseout'
  | 'blur'
  | 'mouseup'
  | 'mousedown';
type EventHandlerType = (a: any) => void;

type Trigger = 'click' | 'hover';

@Component
export default class ClPopper extends Vue {
  @Prop({
    type: Boolean,
    default: false
  })
  public appendToBody!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public arrow!: boolean;

  @Prop(String)
  public boundariesSelector!: string;

  @Prop({
    type: Boolean,
    default: false
  })
  public defaultShow!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public disabled!: boolean;

  @Prop(Boolean)
  public forceShow!: boolean;

  @Prop({
    type: Number,
    default: 10
  })
  public enterDelay!: number;

  @Prop({
    type: Number,
    default: 10
  })
  public leaveDelay!: number;

  /**
   * If true, the popper will be visible.
   */
  @Prop(Boolean)
  public open!: boolean;

  @Prop({
    type: Object as () => PopperOptions,
    default: () => ({})
  })
  public options!: PopperOptions;

  /**
   * Tooltip placement
   */
  @Prop({
    type: String as () => Placement,
    default: 'auto'
  })
  public placement!: Placement;

  @Prop({
    type: String,
    default: ''
  })
  public transition!: string;

  @Prop({
    type: String,
    default: ''
  })
  public transitionDuration!: string;

  @Prop({
    type: String as () => Trigger,
    default: 'hover'
  })
  public trigger!: Trigger;

  private appendedArrow: boolean = false;
  private appendedToBody: boolean = false;
  private popper!: Node;
  private popperJS: Popper | null = null;
  private popperOptions: PopperOptions = {};
  private referenceElm!: Node;
  private showPopper: boolean = false;
  private timer?: any;

  public appendArrow(element: Node) {
    if (this.appendedArrow) {
      return;
    }
    this.appendedArrow = true;
    const arrow = document.createElement('div');
    arrow.setAttribute('x-arrow', '');
    arrow.className = 'cl-popper__arrow';
    element.appendChild(arrow);
  }

  public updatePopper() {
    this.popperJS ? this.popperJS.scheduleUpdate() : this.createPopper();
  }

  @Watch('placement')
  public handlePlacementChange(placement: Placement) {
    this.placement = placement;
    this.popperOptions = Object.assign(this.popperOptions, this.options, {
      placement
    });
  }

  @Watch('showPopper')
  public onShowPopperChange(newVal: boolean, oldVal: boolean) {
    if (newVal === true && oldVal === false) {
      this.$emit('show', true);
      this.updatePopper();
    } else {
      this.$emit('hide');
    }
  }

  @Watch('forceShow', { immediate: true })
  public onForceShowChange(forceShow: boolean) {
    this[forceShow ? 'doShow' : 'doClose']();
  }

  @Watch('disabled')
  public onDisabledChange(disabled: boolean) {
    if (disabled) {
      this.showPopper = false;
    }
  }

  public createPopper() {
    this.$nextTick(() => {
      if (this.arrow) {
        this.appendArrow(this.popper);
      }
      if (this.appendToBody && !this.appendedToBody) {
        this.appendedToBody = true;
        document.body.appendChild(this.popper.parentElement as Node);
      }
      if (this.popperJS && this.popperJS.destroy) {
        this.popperJS.destroy();
      }
      if (this.boundariesSelector) {
        const boundariesElement = document.querySelector(
          this.boundariesSelector
        );
        if (boundariesElement) {
          this.popperOptions.modifiers = Object.assign(
            {},
            this.popperOptions.modifiers
          );
          this.popperOptions.modifiers.preventOverflow = Object.assign(
            {},
            this.popperOptions.modifiers.preventOverflow
          );
          this.popperOptions.modifiers.preventOverflow.boundariesElement = boundariesElement;
        }
      }
      this.popperOptions.onCreate = () => {
        this.$emit('created', this);
        this.$nextTick(this.updatePopper);
      };
      this.popperJS = new Popper(
        this.referenceElm as Element,
        this.popper as Element,
        this.popperOptions
      );
    });
  }

  public doDestroy() {
    if (this.showPopper) {
      return;
    }
    if (this.popperJS) {
      this.popperJS.destroy();
      this.popperJS = null;
    }
    if (this.appendedToBody) {
      this.appendedToBody = false;
      document.body.removeChild(this.popper!.parentElement as Node);
    }
  }

  public doShow() {
    this.showPopper = true;
  }

  public doClose() {
    if (!this.forceShow) {
      this.showPopper = false;
    }
  }

  public doToggle() {
    this.showPopper = this.forceShow ? true : !this.showPopper;
  }

  public elementContains(elm: Node, otherElm: Node) {
    if (typeof elm.contains === 'function') {
      return elm.contains(otherElm);
    }
    return false;
  }

  public handleDocumentClick(event: Event): void {
    const target: EventTarget | null = event.target;
    const targetNode: Node = target as Node;
    if (
      !this.$el ||
      !this.referenceElm ||
      this.elementContains(this.$el, targetNode) ||
      this.elementContains(this.referenceElm, targetNode) ||
      !this.popper ||
      this.elementContains(this.popper, targetNode)
    ) {
      return;
    }
    this.$emit('documentClick', this);
    if (this.forceShow) {
      return;
    }
    this.showPopper = false;
  }

  public onMouseOver() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.showPopper = true;
    }, this.enterDelay);
  }

  public onMouseOut() {
    if (this.forceShow) {
      return;
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.showPopper = false;
    }, this.leaveDelay);
  }

  public created() {
    const exposedPopperOptions: PopperOptions = {
      placement: this.placement
    };
    this.popperOptions = Object.assign(
      this.popperOptions,
      this.options,
      exposedPopperOptions
    );
  }

  public mounted() {
    this.referenceElm = this.$slots.reference![0].elm as Node;
    this.popper = this.$slots.default![0].elm as Node;

    switch (this.trigger) {
      case 'click':
        on(this.referenceElm, 'click', this.doToggle);
        on(document, 'click', this.handleDocumentClick);
        break;
      case 'hover':
        on(this.referenceElm, 'mouseover', this.onMouseOver);
        on(this.referenceElm, 'focus', this.onMouseOver);
        on(this.popper, 'mouseover', this.onMouseOver);
        on(this.popper, 'focus', this.onMouseOver);
        on(this.referenceElm, 'mouseout', this.onMouseOut);
        on(this.referenceElm, 'blur', this.onMouseOut);
        on(this.popper, 'mouseout', this.onMouseOut);
        on(this.popper, 'blur', this.onMouseOut);
        break;
    }

    if (this.defaultShow) {
      setTimeout(this.doShow, 1000);
    }
  }

  public destroyed() {
    off(this.referenceElm, 'click', this.doToggle);
    off(this.referenceElm, 'mouseup', this.doClose);
    off(this.referenceElm, 'mousedown', this.doShow);
    off(this.referenceElm, 'focus', this.doShow);
    off(this.referenceElm, 'blur', this.doClose);
    off(this.referenceElm, 'mouseout', this.onMouseOut);
    off(this.referenceElm, 'mouseover', this.onMouseOver);
    off(document, 'click', this.handleDocumentClick);
    this.showPopper = false;
    this.doDestroy();
  }
}
